/**
 * Dashboard registry — single source of truth for what surfaces exist.
 *
 * The launcher and the header DashboardMenu both render filtered lists from
 * this array, gated on the current user's permissions. Array order is display
 * order — busiest first, least-used last, Admin at the very end — and the
 * Admin → Permissions columns follow it too (see orderPermissionColumns).
 * To add a new dashboard:
 *   1. Add an entry here.
 *   2. Add a matching route in App.tsx.
 *   3. (If gated) add the permission key to PERMISSION_REGISTRY in
 *      backend/src/services/permissionsService.ts and the PermissionKey union
 *      in frontend/src/hooks/usePermissions.ts.
 *   4. (If it has finer-grained keys, e.g. `*.manage`) list them in
 *      `subPermissions` so their columns sit next to the dashboard's own.
 */

import type { PermissionKey } from '../hooks/usePermissions';

export type DashboardTone = 'accent' | 'spark' | 'plum' | 'forest' | 'teal';

export interface DashboardEntry {
  id: string;
  label: string;
  description: string;
  icon: string;
  route: string;
  /** Omit to make this dashboard available to every authenticated user. */
  permission?: PermissionKey;
  /**
   * Extra permission columns shown right after this dashboard's column in
   * Admin → Permissions. Ordering only — they gate nothing in the picker.
   */
  subPermissions?: ReadonlyArray<PermissionKey>;
  /** Visual tone for the launcher card (icon block + open arrow). */
  tone?: DashboardTone;
}

export const DASHBOARDS: ReadonlyArray<DashboardEntry> = [
  {
    id: 'chat',
    label: 'Chat',
    description: 'Your home for Culture Genesis data, with access to Gmail, Google Drive, Calendar, and the web.',
    icon: '💬',
    route: '/chat',
    tone: 'accent',
  },
  {
    id: 'sales',
    label: 'Sales',
    description: 'Tentpole Calendar, Media Plans, Rate Cards',
    icon: '📈',
    route: '/dashboards/sales',
    permission: 'sales.view',
    subPermissions: ['mediaPlans.manage', 'tentpoleCalendar.manage'],
    tone: 'teal',
  },
  {
    id: 'ad-ops',
    label: 'Ad Ops',
    description: 'Create Campaign Reports, manage Ad Benchmarks, and track Current Campaigns',
    icon: '🎯',
    route: '/dashboards/ad-ops',
    permission: 'adOps.view',
    subPermissions: ['adOps.bundles.manage', 'adOps.campaignReports.manage'],
    tone: 'accent',
  },
  {
    id: 'financials',
    label: 'Financials',
    description: 'Revenue, payouts, and rate cards. Source of truth from ChannelMeter and Airtable.',
    icon: '💰',
    route: '/dashboards/financials',
    permission: 'financials.view',
    subPermissions: ['financials.corrections'],
    tone: 'forest',
  },
  {
    id: 'creators',
    label: 'Creators',
    description: 'Create and Manage Creator Dashboards',
    icon: '🎬',
    route: '/dashboards/creators',
    permission: 'clientReports.manage',
    tone: 'spark',
  },
  {
    id: 'channel-intelligence',
    label: 'Channel Intelligence',
    description: 'Per-channel YouTube performance, advertiser & campaign history, audience, and revenue — plus Ask.',
    icon: '🧠',
    route: '/dashboards/channel-intelligence',
    permission: 'channelIntelligence.view',
    subPermissions: ['channelIntelligence.manage'],
    tone: 'plum',
  },
  {
    id: 'cg-boost',
    label: 'CG Boost',
    description: 'Campaign performance, flagging, and pacing across active partnerships.',
    icon: '🚀',
    route: '/dashboards/cg-boost',
    permission: 'cgBoost.view',
    tone: 'spark',
  },
  {
    id: 'invoicing',
    label: 'Invoicing',
    description: 'Monthly invoicing tracker: IOs, delivery pacing, over-delivery guard, and billing status.',
    icon: '🧾',
    route: '/dashboards/invoicing',
    permission: 'invoicing.view',
    subPermissions: ['invoicing.manage', 'invoicing.billing'],
    tone: 'spark',
  },
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    description: 'Review the last 90–120 days of IOs against invoices — catch un-billed IOs, under-billing, and missed revenue.',
    icon: '🔎',
    route: '/dashboards/reconciliation',
    permission: 'invoicing.view',
    tone: 'forest',
  },
  {
    id: 'takeovers',
    label: 'Takeovers',
    description: 'Creator ad-takeover requests: pipeline, creator request pages, and analytics.',
    icon: '📺',
    route: '/dashboards/takeovers',
    permission: 'takeovers.view',
    subPermissions: ['takeovers.manage'],
    tone: 'teal',
  },
  {
    id: 'admin',
    label: 'Admin',
    description: 'Channel mappings, processing pipelines, scheduled emails, and permissions.',
    icon: '⚙️',
    route: '/admin',
    permission: 'admin.view',
    tone: 'plum',
  },
];

/**
 * The dashboard a path belongs to: its route or anything under it
 * (`/dashboards/sales/…`, `/chat/…`). Null for the launcher (`/`) and pages
 * outside the registry (`/workflow`, `/share/…`).
 */
export function dashboardForPath(pathname: string): DashboardEntry | null {
  return DASHBOARDS.find((entry) => pathname === entry.route || pathname.startsWith(`${entry.route}/`)) ?? null;
}
